import type { FullBaseline, FullCheckinSnapshot, QuickBaseline, QuickCheckinSnapshot } from "@ferrivia/api-contract";
import { useFormatters, useTranslation } from "@ferrivia/i18n";
import { Text } from "@ferrivia/ui";
import { BatteryMedium, Smile } from "lucide-react";
import { ResultTrend } from "../components/ResultTrend";
import { useSymptomLabels } from "../components/SymptomCheckin/useSymptomLabels";
import { AnswerChip, AnswerChips } from "./AnswerChip";
import { ArticleCard } from "./ArticleCard";
import { Disclosure } from "./Disclosure";
import { byChecklistOrder, symptomChange } from "./symptomChange";
import s from "./SymptomsPart.module.css";

type Baseline = QuickBaseline | FullBaseline;

function useMetaLine() {
  const { t } = useTranslation("client");
  const { date } = useFormatters();
  const format = (iso: string) => date.format(new Date(iso));

  return (reportedAt: string, baseline: Baseline | null): string => {
    if (!baseline) {
      return t("summary.checkins.meta", "Ilmoitettu {{date}}", { date: format(reportedAt) });
    }
    if (baseline.source === "previous_summary") {
      return t("summary.checkins.metaPreviousSummary", "Ilmoitettu {{date}} · edellinen raportti {{previous}}", {
        date: format(reportedAt),
        previous: format(baseline.previousSubmittedAt),
      });
    }
    return t("summary.checkins.metaPrevious", "Ilmoitettu {{date}} · edellinen {{previous}}", {
      date: format(reportedAt),
      previous: format(baseline.checkin.createdAt),
    });
  };
}

export interface EnergyMoodCellsProps {
  latest: QuickCheckinSnapshot;
  baseline: QuickBaseline | null;
}

export function EnergyMoodCells({ latest, baseline }: EnergyMoodCellsProps) {
  const { t } = useTranslation("client");
  const metaLine = useMetaLine();

  const cells = [
    {
      id: "energy",
      label: t("symptomCheckin.energyLabel", "Energia"),
      icon: <BatteryMedium aria-hidden size={18} />,
      value: latest.energy,
      before: baseline?.checkin.energy ?? null,
    },
    {
      id: "mood",
      label: t("symptomCheckin.moodLabel", "Mieliala"),
      icon: <Smile aria-hidden size={18} />,
      value: latest.mood,
      before: baseline?.checkin.mood ?? null,
    },
  ].flatMap((cell) => (cell.value === null ? [] : [{ ...cell, value: cell.value }]));

  if (cells.length === 0) {
    return null;
  }

  return (
    <div className={s.block}>
      <div className={s.cells}>
        {cells.map((cell) => (
          <div key={cell.id} className={s.cell}>
            <span className={s.cellLabel}>
              {cell.icon}
              {cell.label}
            </span>
            <span className={s.cellValue}>{t("summary.checkins.percent", "{{value}} %", { value: cell.value })}</span>
            {cell.before !== null && <ResultTrend kind="checkin" current={cell.value} previous={cell.before} unit="%" />}
          </div>
        ))}
      </div>
      <Text as="p" size="xs" className={s.meta}>
        {metaLine(latest.createdAt, baseline)}
      </Text>
    </div>
  );
}

export interface SymptomChangesProps {
  latest: FullCheckinSnapshot;
  baseline: FullBaseline | null;
}

export function SymptomChanges({ latest, baseline }: SymptomChangesProps) {
  const { t } = useTranslation("client");
  const labels = useSymptomLabels();
  const metaLine = useMetaLine();

  const labelFor = (id: string) => (id in labels ? labels[id as keyof typeof labels] : id);
  const current = byChecklistOrder(latest.symptoms);
  const change = baseline ? symptomChange(latest.symptoms, baseline.checkin.symptoms) : { added: [], dropped: [] };

  const noneReported = latest.noneOfThese && current.length === 0;
  const noneReportedLabel = t("summary.symptoms.noneReported", "Ei ilmoitettuja oireita");

  const reported =
    noneReported
      ? noneReportedLabel
      : current.length === 1
        ? t("summary.symptoms.reportedOne", "Ilmoitit 1 oireen")
        : t("summary.symptoms.reportedMany", "Ilmoitit {{n}} oiretta", { n: current.length });

  const changeParts = [
    change.added.length === 0
      ? null
      : change.added.length === 1
        ? t("summary.symptoms.addedOne", "1 uusi")
        : t("summary.symptoms.addedMany", "{{n}} uutta", { n: change.added.length }),
    change.dropped.length === 0
      ? null
      : t("summary.symptoms.dropped", "{{n}} jäi pois", { n: change.dropped.length }),
  ].filter((part): part is string => part !== null);

  const added = new Set(change.added);

  return (
    <div className={s.block}>
      <Disclosure
        sectionId="symptoms"
        foldedLabel={changeParts.length > 0 ? `${reported} · ${changeParts.join(", ")}` : reported}
        openLabel={t("summary.symptoms.hide", "Piilota oireet")}
      >
        <p className={s.question}>
          {t("symptomCheckin.question", "Oletko viimeisen viikon aikana kärsinyt seuraavista oireista?")}
        </p>
        <AnswerChips>
          {noneReported && <AnswerChip>{noneReportedLabel}</AnswerChip>}
          {current.map((id) => (
            <AnswerChip key={id} tag={added.has(id) ? t("summary.symptoms.new", "Uusi") : undefined}>
              {labelFor(id)}
            </AnswerChip>
          ))}
          {change.dropped.map((id) => (
            <AnswerChip key={id} dropped droppedNote={t("summary.symptoms.droppedNote", ", ei enää ilmoitettu")}>
              {labelFor(id)}
            </AnswerChip>
          ))}
        </AnswerChips>
      </Disclosure>
      <Text as="p" size="xs" className={s.meta}>
        {metaLine(latest.createdAt, baseline)}
      </Text>
    </div>
  );
}

export interface SymptomsPartProps {
  quick: { latest: QuickCheckinSnapshot | null; baseline: QuickBaseline | null };
  full: { latest: FullCheckinSnapshot | null; baseline: FullBaseline | null };
}

export function SymptomsPart({ quick, full }: SymptomsPartProps) {
  const { t } = useTranslation("client");

  return (
    <div className={s.root}>
      {quick.latest && <EnergyMoodCells latest={quick.latest} baseline={quick.baseline} />}
      {full.latest && <SymptomChanges latest={full.latest} baseline={full.baseline} />}
      <ArticleCard slotKind="fixed" title={t("summary.articles.symptoms", "Raudanpuutteen oireet")} />
    </div>
  );
}
